import {
  Alert,
  AlertSeverity,
  AlertType,
  AloftPrice,
  CompType,
  ManualAdjustmentKey,
  UnitRateKey,
  isDefined,
  orUndefined,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompPropertyDataState, OrderState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { calculateUnitRateAdjustment } from '../../../../redux/features/selectors/adjustmentSelectors/utils/calculateUnitRateAdjustment.ts';
import { selectManualAdjustmentsEntities } from '../../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectSubjectUnitRates } from '../../../../redux/features/selectors/propertyDataSelectors/unitRateSelectors.ts';
import { getAlertTriggers } from './alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from './compTypeScoping.ts';
import { getSubjectDataForCompType } from './getSubjectDataForCompType.ts';

export type EditablePropertyData = SubjectPropertyDataState['subjectData']['editableData'];

export type UnitRateFeatureCheck = {
  label: string;
  manualKey: ManualAdjustmentKey;
  unitRateKey: UnitRateKey;
  adjustmentCellKeyId: ManualAdjustmentKey;
  threshold: number;
  minAbsoluteUnitDifference?: number;
  unit: string;
  getUnitAmount: (editableData: EditablePropertyData) => number | undefined;
};

const TARGET_CELL_TOOLTIP = 'Adjusted at different unit rate';
const TIPS =
  'Consider applying a uniformed adjustment rate to the Comp, or explain how you derived the unique adjustment rate.';

export const exceedsDeviationThreshold = (
  manualAdjustment: AloftPrice,
  supportedAdjustment: AloftPrice,
  threshold: number,
): boolean => {
  if (supportedAdjustment.equals(AloftPrice.zero)) return false;

  if (threshold === 0) {
    return !manualAdjustment.equals(supportedAdjustment);
  }

  const deviation = manualAdjustment.sub(supportedAdjustment).ratio(supportedAdjustment);
  return isDefined(deviation) && Math.abs(deviation) > threshold;
};

export const checkInconsistentUnitRateAdjustments = ({
  alertType,
  severity,
  featureChecks,
  fixingMethod,
  compSequence,
  fullCompSequence,
  compType,
  manualAdjustments,
  order,
  propertyDataEntities,
  salesComparisonApproach,
  subjectUnitRates,
}: {
  alertType: AlertType;
  severity: AlertSeverity;
  featureChecks: UnitRateFeatureCheck[];
  fixingMethod: Alert['fixingInfo']['fixingMethod'];
  compSequence: string[];
  fullCompSequence?: string[];
  compType?: CompType;
  manualAdjustments?: ReturnType<typeof selectManualAdjustmentsEntities>;
  order: OrderState;
  propertyDataEntities: ReturnType<typeof selectPropertyDataEntities>;
  salesComparisonApproach?: string;
  subjectUnitRates?: ReturnType<typeof selectSubjectUnitRates>;
}): Alert[] => {
  if (salesComparisonApproach !== 'unit_rate' || !manualAdjustments || !subjectUnitRates) {
    return [];
  }

  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;
  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);
  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence.flatMap((id) => {
    const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
      .editableData;
    if (!propertyData) return [];

    const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);

    return featureChecks.flatMap((featureCheck) => {
      const unitRate = subjectUnitRates[featureCheck.unitRateKey];
      if (!unitRate || unitRate.equals(AloftPrice.zero)) return [];

      const manualAdjustment = orUndefined(schAloftPrice.parse)(
        manualAdjustments[id]?.[featureCheck.manualKey],
      );
      if (!manualAdjustment) return [];

      const subjectUnitAmount = featureCheck.getUnitAmount(scopedSubjectData);
      const compUnitAmount = featureCheck.getUnitAmount(propertyData);
      const unitDifference = (subjectUnitAmount ?? 0) - (compUnitAmount ?? 0);
      const absoluteUnitDifference = Math.abs(unitDifference);
      if (
        absoluteUnitDifference === 0 ||
        (isDefined(featureCheck.minAbsoluteUnitDifference) &&
          absoluteUnitDifference <= featureCheck.minAbsoluteUnitDifference)
      ) {
        return [];
      }

      const supportedAdjustment = calculateUnitRateAdjustment({
        subjectUnitAmount,
        compUnitAmount,
        unitRate,
      });
      if (!supportedAdjustment || supportedAdjustment.equals(AloftPrice.zero)) return [];

      if (
        !exceedsDeviationThreshold(manualAdjustment, supportedAdjustment, featureCheck.threshold)
      ) {
        return [];
      }

      const appliedRate = manualAdjustment.div(absoluteUnitDifference);
      const rateDisplay = appliedRate?.toString(true) ?? '';

      return [
        {
          type: getScopedAlertType(alertType, compType),
          status: 'active' as const,
          severity,
          orderType: ['appraisify'] as const,
          message: `${propertyName} ${featureCheck.label} is adjusted at a significantly different unit rate (${rateDisplay}/${featureCheck.unit}).`,
          propertyID: id,
          ...(compType ? { compType } : {}),
          fixingInfo: {
            tips: TIPS,
            pairedCostRouteItem: fixRoute,
            fixingMethod,
            commentAlwaysRequired: false,
            targetCellKey: `${id}-${featureCheck.adjustmentCellKeyId}` as PropertyGridCellKey,
            targetCellTooltip: TARGET_CELL_TOOLTIP,
          },
          triggers,
        },
      ];
    });
  });
};
